import { Component, inject, Input, OnInit } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { SECTION_TYPE, FormFieldConfig, FieldSelectedValue, FIELD_TYPES } from '../../model/form.model';
import { FieldNames } from '../../model/form-fields.model';
import { DYNAMIC_IDENTIFIER } from '../../model/common.model';
import { CommonUtils } from '../../utils/common.utils';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterListContainerComponent } from '../../layout/filter-list-container/filter-list-container.component';

@Component({
  selector: 'app-elite-buy-sell-dashboard',
  standalone: true,
  imports: [DashboardContainerComponent],
  template: `<app-dashboard-container [gridSchema]="gridSchema" [props]="props"></app-dashboard-container>`,
  styleUrls: ['./elite-buy-sell-dashboard.component.scss']
})
export class EliteBuySellDashboardComponent implements OnInit {
  @Input() gridSchema!: any;
  @Input() props: any = {};
  public globalChartsPayload = new ReplaySubject<any>(1);
  public globalFilterFields: FormFieldConfig[] = [];
  public globalTitleFields: any = { [DYNAMIC_IDENTIFIER['globalyear']]: '' };
  private readonly commonUtils = inject(CommonUtils);

  ngOnInit() {
    this.updateGlobalFilter();
    this.emitDefaultDateFilter();
  }

  updateGlobalFilter() {
    if (!this.gridSchema || !Array.isArray(this.gridSchema)) { return; }
    this.gridSchema.forEach((gridElm: any) => {
      if (!gridElm?.inputs) { gridElm.inputs = {}; }
      if (gridElm['type'] === SECTION_TYPE['GlobalFilter']) {
        if (gridElm.inputs?.dynamicFormArray) {
          gridElm.inputs.dynamicFormArray.onControlValueChanged = this.globalFilterOnChange;
          if (gridElm.inputs.dynamicFormArray?.FieldsArray) {
            this.resetFieldsToDefault(gridElm.inputs.dynamicFormArray.FieldsArray);
            this.globalFilterFields = gridElm.inputs.dynamicFormArray.FieldsArray;
          }
        }
      } else {
        if (gridElm.component === FilterListContainerComponent || gridElm.type === SECTION_TYPE.List) {
          gridElm.inputs.filterSchema ??= {};
          gridElm.inputs.filterSchema['globalPayloadSubject'] = this.globalChartsPayload;
        } else {
          gridElm.inputs['globalPayloadSubject'] = this.globalChartsPayload;
        }
      }
    });
  }

  private emitDefaultDateFilter(): void {
    const startDateField = this.globalFilterFields.find(field => field['fieldName'] === FieldNames.StartDate);
    if (startDateField) {
      this.globalFilterOnChange({ FieldName: startDateField['fieldName'], Value: startDateField['selectedValue'], FieldArray: this.globalFilterFields });
    }
  }


  private resetFieldsToDefault(fields: FormFieldConfig[]): void {
    fields.forEach(field => {
      field['selectedValue'] = field['fieldType'] === FIELD_TYPES.DATE
        ? this.parseDefaultDate(field['defaultValue'])
        : (field['defaultValue'] ?? '');
    });
  }

  private parseDefaultDate(value: any): Date | '' {
    if (!value) { return ''; }
    if (value instanceof Date) { return value; }
    const parts = String(value).split('/');
    if (parts.length === 3) {
      const month = Number(parts[0]) || 1;
      const day = Number(parts[1]) || 1;
      const year = Number(parts[2]) || new Date().getFullYear();
      return new Date(year, month - 1, day);
    }
    return '';
  }

  globalFilterOnChange = (event: FieldSelectedValue) => {
    let selectedField: FormFieldConfig | null = null;
    let startDate = '';
    let endDate = '';
    event?.FieldArray?.forEach(field => {
      if (field['isDependentOn'] === event['FieldName']) {
        field['selectedValue'] = '';
      } else if (field['fieldName'] === event['FieldName']) {
        selectedField = field;
      }
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        if (field['fieldName'] === FieldNames.StartDate) {
          startDate = this.formatDate(field['selectedValue']);
        }
        if (field['fieldName'] === FieldNames.endDate) {
          endDate = this.formatDate(field['selectedValue']);
        }
      }
    });

    // Only emit when both dates are present and valid
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) {
        this.commonUtils.showMatSnackBar('Start Date cannot be lesser than End Date');
        return;
      }
      const filterPayload = {
        FieldName: selectedField?.['fieldName'] ?? '',
        Value: {
          body: { startDate, endDate },
          title: structuredClone(this.globalTitleFields)
        }
      };
      this.globalChartsPayload.next(filterPayload);
    }
  }

  private formatDate(value: any): string {
    if (!value) { return ''; }
    if (value instanceof Date) {
      const yyyy = value.getFullYear();
      const mm = String(value.getMonth() + 1).padStart(2, '0');
      const dd = String(value.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    const parts = String(value).split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return String(value);
  }
}
